import { debounce } from "lodash";
import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { ToastTypes, withToast } from "../../contexts/toastr.context";
import useModel from "../../hooks/use-model.hook";
import { CommunityService, GroupService } from "../../_service";
import I18 from "../atoms/i18";
import { Avatar } from "../avatar.component";
import IconButton2 from "../icon-button-2-component";
import IconMenu2 from "../icon-menu-2.component";
import { Loader } from "../loader.component";
import { Modal } from "../modal.component";
import MakeAdminModal from "../roles/make-admin-modal.component";
import UIcon from "../uicon-component";
import UpdateRoleModal from "./update-role-modal.component";

/**
 * Get community subscribers with group role
 * @param {*} param0
 * @returns
 */
function SidebarCommunityCardComponent({
  user,
  group,
  community,
  addGroupMember,
  updateGroupMember,
  removeGroupMember,
  updateSubscriberRef = null,
  addToast,
}) {
  // get list of subscribers
  const [subscribers, setSubscribers] = useState([]);
  const [subscribersCount, setSubscribersCount] = useState(0);

  // helps with pagination
  const [isLoadingMoreSubscribers, setIsLoadingMoreSubscribers] =
    useState(false);
  const [noMoreSubscribers, setNoMoreSubscribers] = useState(false);
  const [page, setPage] = useState(1);

  const [isLoading, setIsLoading] = useState(false);
  const [isSubscriberListVisible, setIsSubscriberListVisible] = useState(false);

  // for admin role related changes
  const [isMakeAdminModalVisible, setIsMakeAdminModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isUpdateRoleModalVisible, setIsUpdateRoleModalVisible] =
    useState(false);
  const innerRef = useRef();

  const {
    profileModelState: { setShowProfileModel, setIdToViewProfile },
  } = useModel();

  // search filter
  const [query, setQuery] = useState("");

  // load the subscriber list once, or per community or group change
  useEffect(() => {
    if (["admin"].includes(community.myRole)) {
      setIsLoading(true);
      CommunityService.getCommunitySubscribersPaginated(
        user,
        community.id,
        10,
        1,
        query,
        group.id
      )
        .then(({ users, total }) => {
          setSubscribers(users);
          setSubscribersCount(total);
          setIsLoading(false);
        })
        .catch((error) => {
          console.error({ error });
          setIsLoading(false);
        });
    }
  }, [user, community.id, community.myRole]);

  const handleSearchDebounced = React.useCallback(
    debounce((query) => {
      if (["admin"].includes(community.myRole)) {
        setIsLoading(true);
        CommunityService.getCommunitySubscribersPaginated(
          user,
          community.id,
          10,
          1,
          query,
          group.id
        )
          .then(({ users, total }) => {
            setSubscribers(users);
            setSubscribersCount(total);
            setIsLoading(false);
            setNoMoreSubscribers(false);
          })
          .catch((error) => {
            console.error({ error });
            setIsLoading(false);
          });
      }
    }, 1000),
    [community.myRole]
  );

  const subscribersList = subscribers;

  const updateSubscriber = (subscriber) => {
    const newSubscribers = [...subscribers];
    const memberIndex = newSubscribers.findIndex((m) => m.id === subscriber.id);
    newSubscribers.splice(memberIndex, 1, subscriber);
    setSubscribers(newSubscribers);
  };

  if (updateSubscriberRef) {
    updateSubscriberRef.current = updateSubscriber;
  }

  const removeSubscriber = (subscriber) => {
    const newSubscribers = [...subscribers];
    const memberIndex = newSubscribers.findIndex((m) => m.id === subscriber.id);
    newSubscribers.splice(memberIndex, 1);
    setSubscribers(newSubscribers);
  };

  const onScroll = React.useCallback(() => {
    if (innerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = innerRef.current;
      if (scrollTop + clientHeight === scrollHeight) {
        // subscribers list lazy loading you're at the bottom of the page
        // do this when we reach end
        if (!isLoadingMoreSubscribers && !noMoreSubscribers) {
          const pageCount = page + 1;
          // if we are not already loading more subscribers, load more subscribers
          setIsLoadingMoreSubscribers(true);
          CommunityService.getCommunitySubscribersPaginated(
            user,
            community.id,
            10,
            pageCount,
            query,
            group.id
          )
            .then((response) => {
              if (response.users.length === 0) {
                setNoMoreSubscribers(true);
              } else {
                setSubscribers([...subscribers, ...response.users]);
                setPage(pageCount);
              }
              setIsLoadingMoreSubscribers(false);
            })
            .catch((error) => {
              console.error({ error });
              setIsLoading(false);
            });
        }
      }
    }
  }, [isLoadingMoreSubscribers, noMoreSubscribers, page, query, subscribers]);

  // if not admin or moderator, show nothing
  if (community.myRole !== "admin") {
    return <></>;
  }

  return (
    <div className="rounded bg-card">
      {/* <div
        className="py-2 px-4 flex rounded justify-between border-b  border-border items-center cursor-pointer"
        onClick={(e) => {
          if (community.myRole === "admin") {
            setIsSubscriberListVisible(true);
          }
        }}>
        <span className="sidebar-heading">
          {subscribersCount} <I18>Members</I18>
        </span>
        {isLoading ? (
          <div className="p-1">
            <Loader />
          </div>
        ) : (
          <IconButton icon="left-arrow" alt="More" rotate={180} />
        )}
      </div> */}
      {/* subscriber list */}
      <Modal
        className="bg-card"
        setActive={setIsSubscriberListVisible}
        active={isSubscriberListVisible}
        padding={false}>
        {/* headers */}
        <div className="flex items-center justify-between bg-card py-2 pl-4 pr-2">
          <span className="font-bold text-secondary-foreground">
            {community.name} - {subscribersCount} <I18>Subscribers</I18>
          </span>
          <IconButton2
            hoverable={true}
            icon="cross"
            size="xs"
            iconClass="text-secondary-foreground/70 text-xs"
            onClick={() => {
              setIsSubscriberListVisible(false);
            }}
          />
        </div>
        {/* body */}
        <div className="bg-background p-5 pb-10 text-secondary-foreground">
          {/* search bar */}
          <div className="mb-4 flex items-center rounded border border-border bg-background bg-card px-4 py-1">
            <UIcon
              icon="search"
              className="text-xs text-secondary-foreground/70"
            />
            <input
              type="text"
              placeholder="Search"
              value={query}
              onChange={(e) => {
                setIsLoading(true);
                setQuery(e.target.value);
                handleSearchDebounced(e.target.value);
              }}
              className="flex-grow bg-transparent px-4 py-1 focus:outline-none"
            />
            {query.length > 0 ? (
              <i
                onClick={() => {
                  setIsLoading(true);
                  setQuery("");
                  handleSearchDebounced("");
                }}
                className="fi fi-rr-cross cursor-pointer pr-1 text-sm text-secondary-foreground/85"
              />
            ) : null}
          </div>
          {/* subscribers list */}
          {isLoading ? (
            <div className="p-1">
              <Loader />
            </div>
          ) : (
            <></>
          )}
          <div
            onScroll={onScroll}
            ref={innerRef}
            className="h-96 overflow-y-scroll">
            {subscribersList.map((member) => (
              <div
                className="m-3 flex cursor-pointer items-center justify-between"
                key={member.id}>
                <div className="flex items-center justify-center">
                  <Avatar
                    user={member}
                    onClick={() => {
                      // open the user page
                      // window.open(
                      //   createUserWallPageRoute(member.id, "activity")
                      // );

                      setIdToViewProfile(member.id);
                      setShowProfileModel(true);
                    }}
                    tag={
                      <>
                        {member.communityRole === "admin" ? (
                          <div className="text-xxs mx-2 rounded bg-blue-50 px-1 font-semibold text-primary">
                            <I18>Community Admin</I18>
                          </div>
                        ) : (
                          <></>
                        )}
                        {member.groupRole === "admin" ? (
                          <div className="text-xxs mx-2 rounded bg-blue-50 px-1 font-semibold text-primary">
                            <I18>Admin</I18>
                          </div>
                        ) : member.groupRole === "moderator" ? (
                          <div className="text-xxs mx-2 rounded bg-green-50 px-1 font-semibold text-green-500">
                            <I18>Moderator</I18>
                          </div>
                        ) : (
                          <></>
                        )}
                      </>
                    }
                    extraInfo={[member.mobile, member.email]
                      .filter((e) => e)
                      .join(" · ")}
                  />
                </div>
                {/* show approval for user, when community needs verification */}
                {community.myRole === "admin" &&
                community.needsAdminVerification &&
                member.communityRole !== "admin" &&
                !member.communityAdminVerified ? (
                  <div
                    className="text-xxs mx-2 rounded border border-green-500 bg-green-50 px-2 py-1 font-semibold text-green-500"
                    onClick={(e) => {
                      CommunityService.verifyUserByAdmin(
                        user,
                        community.id,
                        member.id
                      )
                        .then(() => {
                          updateSubscriber({
                            ...member,
                            communityAdminVerified: true,
                          });
                        })
                        .catch((error) => {
                          console.error({ error });
                        });
                    }}>
                    <I18>Accept</I18>
                  </div>
                ) : (
                  <></>
                )}
                {/* show request to admin */}
                {community.myRole === "admin" ? (
                  <div>
                    {/* group admin operations */}
                    <IconMenu2
                      dropdownClassName="w-56"
                      hideOnEmpty
                      actions={[
                        {
                          icon: "edit",
                          label: "Change Role",
                          onClick: () => {
                            setSelectedUser(member);
                            setIsUpdateRoleModalVisible(true);
                          },
                        },
                        {
                          // button to add to group
                          icon: "user-add",
                          label: member.groupRole !== "" ? "" : "Add to group", // add only memeber where not already part of group
                          onClick: (e) => {
                            // update the user role to user, it adds the user too
                            GroupService.updateUserRole(
                              user,
                              group.id,
                              member.id,
                              "user"
                            )
                              .then(({ group }) => {
                                // update the current member
                                addGroupMember(member);
                                // update the subscriber in list
                                updateSubscriber({
                                  ...member,
                                  groupRole: "user",
                                });
                                // update email
                                addToast(
                                  `${member.name} is now part of` + group.name,
                                  "",
                                  ToastTypes.success
                                );
                              })
                              .catch((err) => {
                                console.log({
                                  message:
                                    "Error while updating the group member role",
                                  err,
                                });
                                addToast(
                                  "Error while updating the group member role",
                                  "",
                                  ToastTypes.error
                                );
                              });
                          },
                        },
                        {
                          // button to remove group
                          icon: "user-delete",
                          label: ["admin", ""].includes(member.groupRole)
                            ? ""
                            : "Remove from group", // cant remove admin
                          onClick: (e) => {
                            GroupService.removeUser(user, group.id, member.id)
                              .then(() => {
                                // TODO: add the current member
                                removeGroupMember(member);
                                updateSubscriber({
                                  ...member,
                                  groupRole: "",
                                });
                                addToast(
                                  `${member.name} is removed from` + group.name,
                                  "",
                                  ToastTypes.success
                                );
                              })
                              .catch((err) => {
                                console.log({ err });
                                addToast(
                                  "Error while removing the group member",
                                  "",
                                  ToastTypes.error
                                );
                              });
                          },
                          actionType: "alert",
                        },
                        /*      { // show make user admin modal
                                                             icon: 'user-remove',
                                                             label: member.id !== user.id && member.communityRole === "admin" ? "Remove Community Admin" : "",
                                                             onClick: (e) => {
                                                                 // call the api to remove admin
                                                                 CommunityService.removeUserAsAdminOfWholeCommunity(user, community.id, member.id)
                                                                     .then(res => {
                                                                         // addToast('Role successfully updated', '', ToastTypes.success);
                                                                         updateSubscriber({ ...member, communityRole: "user" });
                                                                         addToast(`${member.name} is removed as admin of` + community.name, "", ToastTypes.success);
                                                                     })
                                                                     .catch(err => {
                                                                         // addToast(err.message, '', ToastTypes.danger);
                                                                         console.log("some error occurred", { err });
                                                                         addToast("Error while removing the community admin", "", ToastTypes.error);
                                                                     });
                                                             },
                                                             actionType: 'alert'
                                                         }, */
                        {
                          // show make user admin modal
                          icon: "cross",
                          label:
                            member.id !== user.id ? "Kick from community" : "",
                          onClick: (e) => {
                            // call the api to remove admin
                            CommunityService.removeUserFromCommunity(
                              user,
                              community.id,
                              member.id
                            )
                              .then((res) => {
                                // addToast('Role successfully updated', '', ToastTypes.success);
                                removeSubscriber(member);
                                addToast(
                                  `${member.name} is removed from` +
                                    community.name,
                                  "",
                                  ToastTypes.success
                                );
                              })
                              .catch((err) => {
                                // addToast(err.message, '', ToastTypes.danger);
                                console.log("some error occurred", { err });
                                addToast(
                                  "Error while removing the community member",
                                  "",
                                  ToastTypes.error
                                );
                              });
                          },
                          actionType: "alert",
                        },
                      ]}
                    />
                  </div>
                ) : (
                  <></>
                )}
              </div>
            ))}
          </div>
          {isLoadingMoreSubscribers ? (
            <div className="p-1">
              <Loader />
            </div>
          ) : null}
        </div>
        {/* make admin modal */}
        <MakeAdminModal
          activeGroup={group}
          active={isMakeAdminModalVisible}
          setActive={setIsMakeAdminModalVisible}
          updateGroupMember={updateGroupMember}
          updateSubscriber={updateSubscriber}
          selectedUser={selectedUser}
        />
      </Modal>
      {/* update role modal */}
      <UpdateRoleModal
        selectedUser={isUpdateRoleModalVisible && selectedUser}
        activeGroup={group}
        updateGroupMember={updateGroupMember}
        updateSubscriber={updateSubscriber}
        unselectUser={(e) => {
          setIsUpdateRoleModalVisible(false);
          setSelectedUser(null);
        }}
      />
    </div>
  );
}

const SidebarCommunityCard = connect((s) => ({
  user: s.auth,
  group: s.activeGroup,
  community: s.community,
}))(withToast(SidebarCommunityCardComponent));

export default SidebarCommunityCard;
